import { faCircleCheck, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useWallet } from '@solana/wallet-adapter-react'
import { Connection, Transaction } from '@solana/web3.js'
import { useState } from 'react'
import { WalletContext } from '~/components/WalletContext'
import { useCurrency } from '~/features/bank/hooks'
import { DecimalPlacesValidator, PositiveNumberValidator } from '~/features/bank/utils'
import { describeWalletError } from '~/features/wallet/errors'
import { useConfirmTransaction } from '../api/useConfirmTransaction'
import { useMakeTransaction } from '../api/useMakeTransaction'
import { DepositList } from './DepositList'
import { WalletSelector } from './WalletSelector'

const depositDefaults = {
    'usdc-spl': [5, 10, 25, 50, 100],
    solana: [0.05, 0.1, 0.25, 0.5, 1],
}

export const Deposit = () => {
    return (
        <WalletContext>
            <div className='flex flex-col gap-2'>
                <WalletSelector />
                <PayButton />
                <DepositList />
            </div>
        </WalletContext>
    )
}

const PayButton = () => {
    const { publicKey, sendTransaction } = useWallet()
    const { mutate: makeTransaction, isLoading } = useMakeTransaction()
    const [amount, setAmount] = useState('')
    // Success is worth stating outright. The deposit list does show a row turning green, but that
    // is a log -- it reports what happened without ever telling you it worked.
    const [confirmed, setConfirmed] = useState(false)
    const [payError, setPayError] = useState<string>()
    const { mutate: confirmTransaction } = useConfirmTransaction({ onConfirmed: () => setConfirmed(true) })
    const { currency } = useCurrency()
    const defaults = depositDefaults[currency.name] || []

    const amountOnChange = (e) => {
        const value = e.target.value
        if (
            [PositiveNumberValidator, DecimalPlacesValidator(currency.decimals)].every((validator) => validator(value))
        ) {
            setAmount(value)
        }
    }
    const pay = (e) => {
        e.preventDefault()
        setConfirmed(false)
        setPayError(undefined)
        makeTransaction(
            { wallet: publicKey.toString(), amount: amount, deposit_type: currency.deposit_type },
            {
                onSuccess: async (data) => {
                    const { uuid, tx, cluster_url } = data
                    const connection = new Connection(cluster_url, 'confirmed')
                    const transaction = Transaction.from(Buffer.from(tx, 'base64'))
                    sendTransaction(transaction, connection)
                        .then((signature) => {
                            setAmount('')
                            confirmTransaction({ uuid, signature })
                        })
                        // A failed deposit used to `console.log` and stop there, so declining in
                        // the wallet -- or an RPC that would not take the transaction -- looked
                        // identical to nothing happening at all.
                        .catch((e) => setPayError(describeWalletError(e)))
                },
                onError: () => setPayError('Could not start the deposit. Please try again.'),
            }
        )
    }

    return (
        <form onSubmit={pay} className='flex flex-col gap-4'>
            {confirmed && (
                <div className='flex flex-row items-center gap-3 border border-[#00FF90]/50 bg-[#00FF90]/10 p-3'>
                    <FontAwesomeIcon icon={faCircleCheck} className='shrink-0 text-[#00FF90]' />
                    <div className='flex min-w-0 flex-col'>
                        <span className='text-xs font-black uppercase tracking-wider text-[#00FF90]'>
                            Deposit confirmed
                        </span>
                        <span className='text-2xs text-white/60'>
                            Your balance is updated and you are ready to drop in.
                        </span>
                    </div>
                </div>
            )}
            <label className='flex flex-row items-center gap-2 p-3 min-h-[48px] w-full border border-white/50 focus-within:border-white transition-colors'>
                <img src={currency.img} className='h-6 w-6 mx-1 shrink-0' />
                <input
                    type='text'
                    inputMode='decimal'
                    className='w-full bg-black focus:outline-none placeholder:uppercase placeholder:text-white/20 placeholder:text-xs'
                    placeholder='amount'
                    value={amount}
                    onChange={amountOnChange}
                />
            </label>
            <div className='grid grid-cols-5 gap-2'>
                {defaults.map((defaultAmount) => (
                    <button
                        key={defaultAmount}
                        type='button'
                        className='bg-white/10 p-2 min-h-[44px] text-sm font-bold hover:bg-white/20 transition-colors'
                        onClick={() => setAmount(defaultAmount.toString())}
                    >
                        {defaultAmount}
                    </button>
                ))}
            </div>
            {/* `disabled:opacity-10` left the button all but invisible at exactly the moment
                someone goes looking for it -- it is disabled until *both* a wallet is connected and
                an amount is entered, so a first-time depositor typing an amount saw nothing appear
                to happen. It stays visibly present and reads as "not yet", not as "absent". */}
            <button
                disabled={!publicKey || amount == ''}
                className='flex flex-row items-center justify-center gap-2 bg-br1-gradient w-full p-3 min-h-[48px] rounded text-center font-bold disabled:opacity-40'
            >
                <span>DEPOSIT NOW</span>
                {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
            </button>
            {payError && (
                <p className='text-2xs font-bold leading-relaxed text-red-400 text-center'>{payError}</p>
            )}
            {!publicKey && (
                <p className='text-2xs uppercase tracking-wider text-white/40 text-center'>
                    Connect a wallet above to deposit
                </p>
            )}
        </form>
    )
}
